import { InputColumnConfig } from "../../../components/common/table_input/tableInput";
import { SummaryConfig } from "../../../components/sql_table/table_summary/tableSummary";
import { TableColumnConfig } from "../../../components/sql_table/tableMain";
import { getHoustonDateInfo } from "../../../helper_functions";
import { calculateNetBarrels, calculateCorrectedGravity } from "../../../calculation_functions";
import { carriersMatch } from "../../../helper_functions";
import { rankCarrierCosts, CarrierRateContext } from "../../../carrierCostRanking";

interface InputConfig {
    tableName: string;
    columns: InputColumnConfig[];
}

interface ColumnConfig {
    tableName: string;
    columns: TableColumnConfig[];
    sortOrder?: Record<string, 'asc' | 'desc'>;
    warningRules?: any[];
    enableSettings?: boolean;
    tableSummary?: SummaryConfig[];
    rowRibbon?: {condition: (rowData: any) => boolean; color: string};
    rowColor?: Array<{condition: (rowData: any) => boolean; color: string}>;
    exportColumnOrder?: string[];
}

// Custom crude schedule_table column order for scheduler/schedule_admin roles (dispatch-focused view).
// Any column not listed here is appended afterward in its existing relative order.
const SCHEDULER_CRUDE_COLUMN_ORDER = [
    'status',
    'rid',
    'ticket_number',
    'load_date',
    'producer_name',
    'lease_name',
    'is_reroute',
    'destination_name',
    'carrier_name',
    'product',
    'gross_barrels',
    'net_barrels',
    'bsw',
    'observed_gravity',
    'corrected_gravity',
    'start_temperature',
    'end_temperature',
    'observed_temperature',
    'status_notes',
    'observations',
    'driver_notes',
    'extra_notes',
    'log_lact_photo',
    'notes_photo_bsw',
    'pdf_ticket',
    'photo_vs_ticket',
    'log_gps_match',
    'requestDate',
    'requestMethod',
    'request_notes',
];

// Excel export column order for the same roles (Ops asked to keep the export separate from the on-screen order).
const SCHEDULER_CRUDE_EXPORT_COLUMN_ORDER = [
    'rid',
    'ticket_number',
    'load_date',
    'producer_name',
    'lease_name',
    'destination_name',
    'carrier_name',
    'gross_barrels',
    'net_barrels',
    'bsw',
    'observed_gravity',
    'corrected_gravity',
    'observed_temperature',
    'requestDate',
    'requestMethod',
    'log_lact_photo',
    'notes_photo_bsw',
    'pdf_ticket',
    'photo_vs_ticket',
    'log_gps_match',
    'observations',
    'driver_notes',
    'extra_notes',
    'is_reroute',
    'status',
];

const reorderColumns = (columns: TableColumnConfig[], order: string[]): TableColumnConfig[] => {
    const remaining = new Map(columns.map(col => [col.colName, col]));
    const ordered: TableColumnConfig[] = [];
    for (const colName of order) {
        const col = remaining.get(colName);
        if (col) {
            ordered.push(col);
            remaining.delete(colName);
        }
    }
    for (const col of columns) {
        if (remaining.has(col.colName)) ordered.push(col);
    }
    return ordered;
};

// Remove the hook call from here - just export the config structure
export const getInputConfigs = (
    activeTable: string,
    producerOptions: any[],
    entityOptions: any[],
    leaseOptions: any[],
    carrierOptions: any[],
    autoScheduleOptions?: {
        producerOptions: any[];
        leaseOptions: any[];
        destinationOptions: any[];
        carrierOptions: any[];
        userName?: string;
    },
): Record<string, InputConfig> => ({
    schedule_table: {
        tableName: 'schedule_table',
        columns: [
            {
                colName: 'producer_id',
                placeholder: 'Producer',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: producerOptions,
                linkedGroup: ['entity_id', 'lease_id'],
                getLinkedFilteredOptions: (g: Record<string, any>, allProducers: any[]) => {
                    if (g.entity_id) {
                        const entity = entityOptions.find(e => Number(e.value) === Number(g.entity_id));
                        if (entity?.current_producer_id)
                            return allProducers.filter(p => Number(p.value) === Number(entity.current_producer_id));
                    }
                    if (g.lease_id) {
                        const lease = leaseOptions.find(l => Number(l.value) === Number(g.lease_id));
                        if (lease?.entity_id) {
                            const entity = entityOptions.find(e => Number(e.value) === Number(lease.entity_id));
                            if (entity?.current_producer_id)
                                return allProducers.filter(p => Number(p.value) === Number(entity.current_producer_id));
                        }
                    }
                    return allProducers;
                }
            },
            {
                colName: 'entity_id',  
                placeholder: 'Entity',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: entityOptions,
                linkedGroup: ['producer_id', 'lease_id'],
                getLinkedFilteredOptions: (g: Record<string, any>, allEntities: any[]) => {
                    if (g.lease_id) {
                        const lease = leaseOptions.find(l => Number(l.value) === Number(g.lease_id));
                        if (lease?.entity_id)
                            return allEntities.filter(e => Number(e.value) === Number(lease.entity_id));
                    }
                    if (g.producer_id) {
                        return allEntities.filter(e => Number(e.current_producer_id) === Number(g.producer_id));
                    }
                    return allEntities;
                }
            },
            {
                colName: 'lease_id',
                placeholder: 'Lease',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: leaseOptions,
                linkedGroup: ['producer_id', 'entity_id'],
                getLinkedFilteredOptions: (g: Record<string, any>, allLeases: any[]) => {
                    if (g.entity_id)
                        return allLeases.filter(l => Number(l.entity_id) === Number(g.entity_id));
                    if (g.producer_id)
                        return allLeases.filter(l => Number(l.producer_id) === Number(g.producer_id));
                    return allLeases;
                }
            },
            {
                colName: 'loadDate',
                placeholder: getHoustonDateInfo().tomorrowString,
                colType: 'date',
                inputType: 'date',
                required: true,
                infoTooltip: 'Date for load pick-up',
            },
            {
                colName: 'requestMethod',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Text', label: 'Text' },
                    { value: 'Call', label: 'Call' },
                    { value: 'Email', label: 'Email' },
                    { value: 'Other', label: 'Other' },
                ],
                required: true,
                placeholder: 'Request method',
                infoTooltip: 'Method used to make request',
            },
            {
                colName: 'requestDate',
                placeholder: getHoustonDateInfo().todayString,
                colType: 'datetime',
                inputType: 'datetime',
                required: true,
                infoTooltip: 'Date - Time request was made',
            },
        ],
    },
    bottoms_schedule_table: {
        tableName: 'bottoms_schedule_table',
        columns: [
            {
                colName: 'producer_id',
                placeholder: 'Producer',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: producerOptions,
                linkedGroup: ['lease_id'],
                getLinkedFilteredOptions: (g: Record<string, any>, allProducers: any[]) => {
                    if (g.lease_id) {
                        const selectedLease = leaseOptions.find(lease => Number(lease.value) === Number(g.lease_id));
                        if (selectedLease?.producer_id)
                            return allProducers.filter(producer => Number(producer.value) === Number(selectedLease.producer_id));
                    }
                    return allProducers;
                }
            },
            {
                colName: 'customer',
                placeholder: 'Customer (auto-filled)',
                colType: 'text',
                required: false,
                inputType: 'input',
                calculateValue: (values) => {
                    const producer = producerOptions.find(p => p.value === values.producer_id);
                    return producer?.label || '';
                },
            },
            {
                colName: 'lease_id',
                placeholder: 'Lease',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: leaseOptions,
                linkedGroup: ['producer_id'],
                getLinkedFilteredOptions: (g: Record<string, any>, allLeases: any[]) => {
                    if (g.producer_id)
                        return allLeases.filter(lease => Number(lease.producer_id) === Number(g.producer_id));
                    return allLeases;
                }
            },
            {
                colName: 'location',
                placeholder: 'Location (auto-filled)',
                colType: 'text',
                required: false,
                inputType: 'input',
                calculateValue: (values) => {
                    const lease = leaseOptions.find(l => l.value === values.lease_id);
                    return lease?.label || '';
                },
            },
            {
                colName: 'loadDate',
                placeholder: getHoustonDateInfo().tomorrowString,
                colType: 'date',
                inputType: 'date',
                required: true,
                infoTooltip: 'Date for load pick-up',
            },
            {
                colName: 'requestMethod',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Text', label: 'Text' },
                    { value: 'Call', label: 'Call' },
                    { value: 'Email', label: 'Email' },
                    { value: 'Other', label: 'Other' },
                ],
                required: true,
                placeholder: 'Request method',
                infoTooltip: 'Method used to make request',
            },
            {
                colName: 'requested_on',
                placeholder: getHoustonDateInfo().todayString,
                colType: 'date',
                inputType: 'date',
                required: true,
                infoTooltip: 'Date request was made',
            },
        ],
    },
    ngl_schedule_table: {
        tableName: 'ngl_schedule_table',
        columns: [
            {
                colName: 'producer_id',
                placeholder: 'Producer',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: producerOptions,
                linkedGroup: ['lease_id'],
                getLinkedFilteredOptions: (g: Record<string, any>, allProducers: any[]) => {
                    if (g.lease_id) {
                        const selectedLease = leaseOptions.find(lease => Number(lease.value) === Number(g.lease_id));
                        if (selectedLease?.producer_id)
                            return allProducers.filter(producer => Number(producer.value) === Number(selectedLease.producer_id));
                    }
                    return allProducers;
                }
            },
            {
                colName: 'customer',
                placeholder: 'Customer (auto-filled)',
                colType: 'text',
                required: false,
                inputType: 'input',
                calculateValue: (values) => {
                    const producer = producerOptions.find(p => p.value === values.producer_id);
                    return producer?.label || '';
                },
            },
            {
                colName: 'lease_id',
                placeholder: 'Lease',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: leaseOptions,
                linkedGroup: ['producer_id'],
                getLinkedFilteredOptions: (g: Record<string, any>, allLeases: any[]) => {
                    if (g.producer_id)
                        return allLeases.filter(lease => Number(lease.producer_id) === Number(g.producer_id));
                    return allLeases;
                }
            },
            {
                colName: 'location',
                placeholder: 'Location (auto-filled)',
                colType: 'text',
                required: false,
                inputType: 'input',
                calculateValue: (values) => {
                    const lease = leaseOptions.find(l => l.value === values.lease_id);
                    return lease?.label || '';
                },
            },
            {
                colName: 'loadDate',
                placeholder: getHoustonDateInfo().tomorrowString,
                colType: 'date',
                inputType: 'date',
                required: true,
                infoTooltip: 'Date for load pick-up',
            },
            {
                colName: 'requestMethod',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Text', label: 'Text' },
                    { value: 'Call', label: 'Call' },
                    { value: 'Email', label: 'Email' },
                    { value: 'Other', label: 'Other' },
                ],
                required: true,
                placeholder: 'Request method',
                infoTooltip: 'Method used to make request',
            },
            {
                colName: 'requestDate',
                placeholder: getHoustonDateInfo().todayString,
                colType: 'date',
                inputType: 'date',
                required: true,
                infoTooltip: 'Date request was made',
            },
        ],
    },
    
    // ── Vac Truck Schedule — writes to bottoms_schedule_table (vac_schedule_table_vs is a view) ──
    vac_schedule_table_vs: {
        tableName: 'vac_schedule_table_vs',
        columns: [
            {
                colName: 'producer_id',
                placeholder: 'Producer',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: producerOptions,
                linkedGroup: ['lease_id'],
                getLinkedFilteredOptions: (g: Record<string, any>, allProducers: any[]) => {
                    if (g.lease_id) {
                        const selectedLease = leaseOptions.find(lease => Number(lease.value) === Number(g.lease_id));
                        if (selectedLease?.producer_id)
                            return allProducers.filter(producer => Number(producer.value) === Number(selectedLease.producer_id));
                    }
                    return allProducers;
                }
            },
            {
                colName: 'lease_id',
                placeholder: 'Lease',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: leaseOptions,
                linkedGroup: ['producer_id'],
                getLinkedFilteredOptions: (g: Record<string, any>, allLeases: any[]) => {
                    if (g.producer_id)
                        return allLeases.filter(lease => Number(lease.producer_id) === Number(g.producer_id));
                    return allLeases;
                }
            },
            {
                colName: 'carrier_id',
                placeholder: 'Carrier',
                colType: 'number',
                required: true,
                inputType: 'dropdown',
                dropdownOptions: carrierOptions,
            },
            {
                colName: 'loadDate',
                placeholder: getHoustonDateInfo().tomorrowString,
                colType: 'date',
                inputType: 'date',
                required: true,
                infoTooltip: 'Date for vac truck service',
            },
            {
                colName: 'type_of_work',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Standard Vacuum',               label: 'Standard Vacuum' },
                    { value: 'Freshwater',                    label: 'Freshwater' },
                    { value: 'Washout',                       label: 'Washout' },
                    { value: 'Disposal',                      label: 'Disposal' },
                    { value: 'Standard Vacuum + Freshwater',  label: 'Standard Vacuum + Freshwater' },
                    { value: 'Standard Vacuum + Disposal',    label: 'Standard Vacuum + Disposal' },
                ],
                placeholder: 'Type of Work',
            },
            {
                colName: 'est_hours',
                placeholder: 'Est. Hours',
                colType: 'number',
            },
            {
                colName: 'requestMethod',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Text',  label: 'Text' },
                    { value: 'Call',  label: 'Call' },
                    { value: 'Email', label: 'Email' },
                    { value: 'Other', label: 'Other' },
                ],
                required: true,
                placeholder: 'Request method',
            },
            {
                colName: 'requested_on',
                placeholder: getHoustonDateInfo().todayString,
                colType: 'date',
                inputType: 'date',
                required: true,
                infoTooltip: 'Date request was made',
            },
        ],
    },
});

export const getColConfigs = (
    activeTable: string,
    producerOptions: any[],
    entityOptions: any[],
    leaseOptions: any[],
    destinationOptions: any[],
    carrierOptions: any[],
    orphanedTicketOptions: { oi_ticket_number: string; oi_service_date: string | null }[] = [],
    userRole?: string,
    carrierRateContext?: CarrierRateContext | null,
): Record<string, ColumnConfig> => {
    const configs: Record<string, ColumnConfig> = ({

    schedule_table: {
        tableName: 'schedule_table',
        rowRibbon: {
            condition: (rowData: any) => rowData.available_pdf === 1,
            color: 'var(--color-red-pdf)'
        },
        rowColor: [
            {condition: (rowData: any) => rowData.status === 'Canceled', color: 'var(--color-grey-light-darker)'},
            {condition: (rowData: any) => rowData.status === 'Completed', color: 'var(--color-green-complete)'},
            {condition: (rowData: any) => rowData.status === 'Rejected', color: 'var(--color-red-light)'},
            {condition: (rowData: any) => rowData.status === 'Scheduled', color: 'var(--color-yellow)'},
        ],
        sortOrder: { 
            'load_date': 'desc',
            'producer_name': 'asc',
        },
        tableSummary: [
            { colName: 'gross_barrels', operation: 'sum', label: 'Gross Barrels' },
            { colName: 'net_barrels', operation: 'sum', label: 'Net Barrels' },
            { colName: 'observed_gravity', operation: 'avg', label: 'Obs. Gravity' },
            { colName: 'observed_temperature', operation: 'avg', label: 'Obs. Temperature' },
            { colName: 'corrected_gravity', operation: 'avg', label: 'Corr. Gravity' },
            { colName: 'bsw', operation: 'avg', label: 'BS&W' },
        ],
        enableSettings: true,
        warningRules: [
            {
                key: 'schedule_table_net_barrel_mismatch',
                type: 'alert',
                colName: 'net_barrels',
                description: 'Net barrel calculation mismatch',
                condition: (rowData: any) => {
                    if (rowData.status !== 'Completed') return false;
                    const [calculatedNet, hasMismatch] = calculateNetBarrels({
                        observed_temperature: rowData.observed_temperature,
                        observed_gravity: rowData.observed_gravity,
                        gross_barrels: rowData.gross_barrels,
                        bsw: rowData.bsw,
                        reported_net: rowData.net_barrels,
                        top_temperature: rowData.start_temperature,
                        bottom_temperature: rowData.end_temperature
                    });
                    
                    // hasMismatch will be true if the difference is > 1
                    if (hasMismatch) {
                        return {
                            isMatch: true,
                            data: {
                                calculatedValue: calculatedNet,
                                reportedValue: rowData.net_barrels
                            }
                        };
                    }
                    return false;
                },
            },
            {
                key: 'schedule_table_gross_too_high',
                type: 'alert',
                colName: 'gross_barrels',
                description: 'Gross too high - possible incorrect split load',
                condition: (rowData: any) => {
                    switch (rowData.producer_id){
                        case 124: //steel reef
                            if (rowData.gross_barrels > 250 || rowData.net_barrels > 250) {
                                return true;
                            }else{
                                return false;
                            }
                        default:
                            if (rowData.gross_barrels > 220 || rowData.net_barrels > 220) {
                                return true;
                            }else{
                                return false;
                            }                   
                    }
                   
                },
            },
            {
                key: 'schedule_table_corrected_gravity_mismatch',
                type: 'alert',
                colName: 'corrected_gravity',
                description: 'Corrected gravity calculation mismatch',
                condition: (rowData: any) => {
                    if (rowData.status !== 'Completed') return false;
        
                    const [calculatedGrav, hasMismatch] = calculateCorrectedGravity({
                        observed_temperature: rowData.observed_temperature,
                        observed_gravity: rowData.observed_gravity,
                        reported_grav: rowData.corrected_gravity,
                    });
                    
                    // hasMismatch will be true if the difference is > 1
                    if (hasMismatch) {
                        return {
                            isMatch: true,
                            data: {
                                calculatedValue: calculatedGrav,
                                reportedValue: rowData.corrected_gravity
                            }
                        };
                    }
                    return false;
                },
            },
            {
                key: 'schedule_table_pdf_not_available',
                type: 'pdf_alert',
                colName: 'available_pdf',
                description: 'PDF not available',
                condition: (rowData: any) => {
                    return rowData.available_pdf === 0 && rowData.status == 'Completed';
                },
            },
            {
                key: 'schedule_table_unassigned_ticket_number',
                type: 'warning',
                colName: 'ticket_number',
                description: 'Unassigned ticket number',
                condition: (rowData: any) => {
                    try {
                        // Parse the pickup_data JSON string
                        const pickupData = JSON.parse(rowData.pickup_data || '[]');
                        
                        // Check if there's any pickup data
                        if (!pickupData || pickupData.length === 0) {
                            return false;
                        }

                        // Check ALL pickup entries for unassigned ticket number
                        return pickupData.some((pickup: any) => 
                            !pickup.ticket_number && new Date(rowData.load_date) < (getHoustonDateInfo().yesterday) &&
                            (rowData.status !== 'Canceled')
                        );

                    } catch (error) {
                        console.error('Error parsing pickup_data:', error);
                        return false;
                    }
                },
            },
            {
                key: 'schedule_table_ticket_number_not_found',
                type: 'alert',
                colName: 'ticket_number',
                description: 'Ticket number not found',
                condition: (rowData: any) => {
                    try {
                        // Parse the pickup_data JSON string
                        const pickupData = JSON.parse(rowData.pickup_data || '[]');
                        
                        // Check if there's any pickup data
                        if (!pickupData || pickupData.length === 0) {
                            return false;
                        }

                        // Check ALL pickup entries for ticket number presence
                        return pickupData.some((pickup: any) => 
                            !pickup.has_carrier_match && pickup.ticket_number
                        );

                    } catch (error) {
                        console.error('Error parsing pickup_data:', error);
                        return false;
                    }
                },
            },
            {
                key: 'schedule_table_duplicate_ticket_number',
                type: 'alert',
                colName: 'ticket_number',
                description: 'Duplicate ticket number',
                condition: (rowData: any) => {
                    try {
                        // Parse the pickup_data JSON string
                        const pickupData = JSON.parse(rowData.pickup_data || '[]');

                        // Check if there's any pickup data
                        if (!pickupData || pickupData.length === 0) {
                            return false;
                        }

                        // Check ALL pickup entries for ticket number presence
                        return pickupData.some((pickup: any) =>
                            pickup.is_duplicate === 1
                        );

                    } catch (error) {
                        console.error('Error parsing pickup_data:', error);
                        return false;
                    }
                },
            },
            {
                key: 'schedule_table_ticket_month_mismatch',
                type: 'alert',
                colName: 'ticket_number',
                description: 'Ticket accounting month/year differs from schedule month/year',
                condition: (rowData: any) => {
                    try {
                        if (!rowData.load_date) return false;

                        // load_date is 'YYYY-MM-DD' - parse directly to avoid timezone shifts
                        const [year, month] = rowData.load_date.split('-').map(Number);
                        const schedulePeriod = year * 100 + month;

                        // Parse the pickup_data JSON string
                        const pickupData = JSON.parse(rowData.pickup_data || '[]');

                        // Check if there's any pickup data
                        if (!pickupData || pickupData.length === 0) {
                            return false;
                        }

                        // pickup_period is the ticket's accounting YYYYMM from crude_main_pickup
                        return pickupData.some((pickup: any) =>
                            pickup.ticket_number && pickup.pickup_period &&
                            Number(pickup.pickup_period) !== schedulePeriod
                        );

                    } catch (error) {
                        console.error('Error parsing pickup_data:', error);
                        return false;
                    }
                },
            },
            {
                key: 'schedule_table_mismatch_producer_value',
                type: 'warning',
                colName: 'producer_name',
                description: 'Mismatch in producer value',
                condition: (rowData: any) => {
                    try {
                        // Parse the pickup_data JSON string
                        const pickupData = JSON.parse(rowData.pickup_data || '[]');
                        
                        // Check if there's any pickup data
                        if (!pickupData || pickupData.length === 0) {
                            return false;
                        }
                        // Check ALL pickup entries for producer mismatch
                        return pickupData.some((pickup: any) => {
                            if (pickup.has_carrier_match) {
                                if (rowData.producer_name !== pickup.carrier_producer) {
                                    return true;
                                }
                            }  
                            return false;
                        });

                    } catch (error) {
                        console.error('Error parsing pickup_data:', error);
                        return false;
                    }
                },
            },
            {
                key: 'schedule_table_mismatch_lease_value',
                type: 'warning',
                colName: 'lease_name',
                description: 'Mismatch in lease value',
                condition: (rowData: any) => {
                    try {
                        // Parse the pickup_data JSON string
                        const pickupData = JSON.parse(rowData.pickup_data || '[]');
                        
                        // Check if there's any pickup data
                        if (!pickupData || pickupData.length === 0) {
                            return false;
                        }
                        // Check ALL pickup entries for lease mismatch
                        return pickupData.some((pickup: any) => {
                            if (pickup.has_carrier_match) {
                                if (pickup.lease_name !== pickup.carrier_lease && pickup.carrier_lease !== pickup.billed_lease_name) {
                                    return true;
                                }
                            }  
                            return false;
                        });

                    } catch (error) {
                        console.error('Error parsing pickup_data:', error);
                        return false;
                    }
                },
            },
            // {
            //     type: 'warning',
            //     colName: 'lease_name',
            //     description: 'Mismatch in tank value',
            //     condition: (rowData: any) => {
            //         try {
            //             // Parse the pickup_data JSON string
            //             const pickupData = JSON.parse(rowData.pickup_data || '[]');
                        
            //             // Check if there's any pickup data
            //             if (!pickupData || pickupData.length === 0) {
            //                 return false;
            //             }

            //             // Check ALL pickup entries for tank mismatch
            //             return pickupData.some((pickup: any) => 
            //                 pickup.has_carrier_match && pickup.carrier_tank && pickup.tank_name !== pickup.carrier_tank
            //             );
            //         } catch (error) {
            //             console.error('Error parsing pickup_data:', error);
            //             return false;
            //         }
            //     },
            // },
            {
                key: 'schedule_table_mismatch_destination_value',
                type: 'warning',
                colName: 'destination_name',
                description: 'Mismatch in destination value',
                condition: (rowData: any) => {
                    try {
                        // Parse the delivery_data JSON string
                        const deliveryData = JSON.parse(rowData.delivery_data || '[]');
                        const carrierTicketData = JSON.parse(rowData.carrier_ticket_data || '{}');

                        // Get all tickets with has_carrier_match = 1
                        const matchedTickets = Object.entries(carrierTicketData).filter(
                            ([_, ticketInfo]: [string, any]) => ticketInfo.has_carrier_match === 1
                        );
                        
                        if (matchedTickets.length === 0 || deliveryData.length === 0) {
                            return false;
                        }

                        // Count pickups and offloads
                        const numPickups = matchedTickets.length;
                        const numOffloads = deliveryData.length;
                        
                        // Check for destination mismatches based on the rules
                        return matchedTickets.some(([ticketNumber, ticketInfo]: [string, any]) => {
                            const pickupSequence = ticketInfo.pickup_sequence;
                            const carrierDestination = ticketInfo.destination;
                            
                            let deliveryToCompare;
                            
                            if (numPickups > 1 && numOffloads === 1) {
                                // Multiple pickups, one offload: compare all pickups to the single offload
                                deliveryToCompare = deliveryData[0];
                            } else if (numPickups === numOffloads) {
                                // Same number of pickups and offloads: compare by sequence (1-1, 2-2, etc)
                                deliveryToCompare = deliveryData.find((d: any) => d.sequence === pickupSequence);
                            } else if (numOffloads > numPickups) {
                                // More offloads than pickups: only compare to offload sequence 1
                                deliveryToCompare = deliveryData.find((d: any) => d.sequence === 1);
                            } else {
                                // Fallback: compare by sequence
                                deliveryToCompare = deliveryData.find((d: any) => d.sequence === pickupSequence);
                            }
                            
                            // If we found a delivery to compare, check for mismatch
                            if (deliveryToCompare) {
                                return carrierDestination !== deliveryToCompare.destination_name;
                            }
                            
                            return false;
                        });
                    } catch (error) {
                        console.error('Error parsing carrier/delivery data:', error);
                        return false;
                    }
                },
            },
            // // temporarily disabling offload lane mismatch flag until data comes through in reports
            // {
            //     type: 'warning',
            //     colName: 'destination_name',
            //     description: 'Mismatch in offload lane value',
            //     condition: (rowData: any) => {
            //         try {
            //             // Parse the delivery_data JSON string
            //             const deliveryData = JSON.parse(rowData.delivery_data || '[]');
                        
            //             // Check if there's any delivery data
            //             if (!deliveryData || deliveryData.length === 0) {
            //                 return false;
            //             }

            //             // Check ALL delivery entries for offload lane mismatch
            //             return deliveryData.some((delivery: any) => 
            //                 delivery.has_carrier_match && 
            //                 delivery.sequence === 1 &&
            //                 delivery.offload_lane !== delivery.carrier_offload_lane
            //             );
            //         } catch (error) {
            //             console.error('Error parsing delivery_data:', error);
            //             return false;
            //         }
            //     },
            // },
            {
                key: 'schedule_table_extra_offload_missing_volume',
                type: 'warning',
                colName: 'destination_name',
                description: 'Extra offload - missing volume',
                condition: (rowData: any) => {
                    try {
                        // Parse the JSON data
                        const carrierTicketData = JSON.parse(rowData.carrier_ticket_data || '{}');
                        const deliveryData = JSON.parse(rowData.delivery_data || '[]');
                        
                        if (!deliveryData || deliveryData.length === 0) {
                            return false;
                        }

                        // Get all tickets with has_carrier_match = 1
                        const matchedTickets = Object.entries(carrierTicketData).filter(
                            ([_, ticketInfo]: [string, any]) => ticketInfo.has_carrier_match === 1
                        );

                        if (matchedTickets.length === 0) {
                            return false;
                        }

                        const numPickups = matchedTickets.length;
                        const numOffloads = deliveryData.length;

                        // Check each secondary delivery (sequence !== 1) to see if it needs volume
                        return deliveryData.some((delivery: any) => {
                            if (!delivery.has_carrier_match || delivery.sequence === 1 || delivery.gross_offload) {
                                return false;
                            }

                            // Determine if this delivery should have been assigned a volume
                            let volumeShouldBeAssigned = false;

                            if (numPickups > 1 && numOffloads === 1) {
                                // Not applicable - this is sequence !== 1, but only 1 offload exists
                                volumeShouldBeAssigned = false;
                            } else if (numPickups === numOffloads) {
                                // Equal: check if there's a matching ticket by sequence and destination
                                const matchingTicket = matchedTickets.find(([_, ticketInfo]: [string, any]) => 
                                    ticketInfo.pickup_sequence === delivery.sequence &&
                                    ticketInfo.destination === delivery.destination_name
                                );
                                volumeShouldBeAssigned = !!matchingTicket;
                            } else if (numOffloads > numPickups) {
                                // More offloads: check if there's a matching ticket by destination
                                const matchingTicket = matchedTickets.find(([_, ticketInfo]: [string, any]) => 
                                    ticketInfo.destination === delivery.destination_name
                                );
                                volumeShouldBeAssigned = !!matchingTicket;
                            }

                            // Show warning only if volume should NOT be assigned (enableVolumeAdjustment = true)
                            // i.e., the delivery needs manual volume entry
                            return !volumeShouldBeAssigned;
                        });
                    } catch (error) {
                        console.error('Error parsing delivery_data:', error);
                        return false;
                    }
                },
            }
        ],
        columns: [
            {
                colName: 'id',
                colHeader: 'ID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'status',
                colHeader: 'Status',
                colType: 'text',
                editable: true,
                // requiresApproval: true,
                // editableLevel: 2,
                // rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"], 
                // approvalLevel: 4,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Scheduled', label: 'Scheduled' },
                    { value: 'Completed', label: 'Completed' },
                    { value: 'Canceled', label: 'Canceled' },
                    { value: 'Rejected', label: 'Rejected' },
                ],
            },
            {
                colName: 'rid',
                colHeader: 'RID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'ticket_number',
                colHeader: 'Ticket Number',
                subTable: 'crude_schedule_pickup',
                colType: 'text',
                enableSidepanel: true,
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'load_date',
                actualColName: 'loadDate',
                colHeader: 'Load Date',
                colType: 'date',
                editable: true,
                requiresApproval: true,
                editableLevel:2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'carrier_name',
                actualColName: 'carrier_id',
                colHeader: 'Carrier',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel:2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
                inputType: 'dropdown',
                dropdownOptions: carrierOptions,
                dependsOn: 'lease_name',
                getFilteredOptions: (leaseNameValue: any, allCarriers: any[], rowData?: any) => {
                    const eligible = (() => {
                        if (!leaseNameValue) return allCarriers;
                        // rowData.lease_name may reference multiple leases on a multi-pickup load
                        // (" / " separated), including reroute segments ("Old -> New") — resolve every
                        // distinct lease name actually on this row so product/region reflect this load only.
                        const leaseNames = String(leaseNameValue)
                            .split(' / ')
                            .flatMap(part => part.split(' -> '))
                            .map(s => s.trim())
                            .filter(Boolean);

                        const matchedLeases = leaseOptions.filter((l: any) => leaseNames.includes(l.label));
                        if (matchedLeases.length === 0) return allCarriers;

                        const products = new Set(matchedLeases.map((l: any) => l.product_type).filter(Boolean));
                        const regions = new Set(matchedLeases.map((l: any) => l.region).filter(Boolean));

                        return allCarriers.filter((carrier: any) => {
                            // Product type — strict: carrier must declare a product used on this row
                            const productMatch = (() => {
                                try {
                                    const types: { type: string }[] = typeof carrier.approved_product_types === 'string'
                                        ? JSON.parse(carrier.approved_product_types)
                                        : carrier.approved_product_types;
                                    if (!Array.isArray(types) || types.length === 0) return false;
                                    return types.some(t => products.has(t.type));
                                } catch { return false; }
                            })();
                            // Operating basin — permissive: carrier with no basins set serves all regions;
                            // otherwise carrier must serve at least one of this row's actual lease regions
                            const regionMatch = (() => {
                                try {
                                    const basins: { basin: string }[] = typeof carrier.operating_basin === 'string'
                                        ? JSON.parse(carrier.operating_basin)
                                        : carrier.operating_basin;
                                    if (!Array.isArray(basins) || basins.length === 0) return true;
                                    if (regions.size === 0) return true;
                                    return basins.some(b => regions.has(b.basin));
                                } catch { return true; }
                            })();
                            return productMatch && regionMatch;
                        });
                    })();

                    // The row's currently-assigned carrier must always be priceable as the
                    // "vs current" baseline, even if it no longer passes today's product/
                    // basin eligibility filter (e.g. approved_product_types was left empty,
                    // or changed since this carrier was assigned) — otherwise rankCarrierCosts
                    // can't find it, silently falls back to comparing against the cheapest
                    // option instead, and every delta looks like it's "vs best", not "vs current".
                    const currentCarrierId = rowData?.carrier_id ?? null;
                    const priceable = (currentCarrierId != null && !eligible.some((c: any) => c.value === currentCarrierId))
                        ? [...eligible, ...allCarriers.filter((c: any) => c.value === currentCarrierId)]
                        : eligible;

                    // Cost-delta labels — see src/carrierCostRanking.ts. Falls back to the
                    // eligible list unchanged (no suffix) when mileage or pricing context
                    // isn't available yet (e.g. pickup/delivery legs not set).
                    const ranked = rankCarrierCosts(
                        priceable,
                        rowData?.total_mileage,
                        carrierRateContext ?? null,
                        currentCarrierId,
                    );
                    return ranked.map(c => ({ ...c, label: c.displayLabel }));
                },
            },
            {
                // Admin-only ops-tracking field: the all-in $ delta between the carrier
                // picked and the cheapest eligible carrier at pick time, persisted
                // server-side (web_edit_row.py). Not visible to schedulers — filtered
                // out of the scheduler/schedule_admin column order below; visible to
                // every other role (finance/accounting included) by default.
                colName: 'carrier_selection_delta',
                colHeader: 'Carrier Selection Delta',
                colType: 'accounting',
                redNegative: true,
                editable: false,
            },
            {
                colName: 'producer_name',
                actualColName: 'producer_id',
                colHeader: 'Producer',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel:2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
                inputType: 'dropdown',
                dropdownOptions: producerOptions,
            },
            {
                colName: 'entity_name',
                actualColName: 'entity_id',
                colHeader: 'Entity',
                colType: 'text',
                editable: false,
                requiresApproval: true,
                editableLevel:2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
                inputType: 'dropdown',
                dropdownOptions: entityOptions,
                dependsOn: 'producer_id',
                getFilteredOptions: (producerId: number, allEntities: any[]) => {
                    const filtered = allEntities.filter(entity => {
                        return Number(entity.current_producer_id) === Number(producerId);
                    });
                    return filtered;
                },
            },
            { 
                colName: 'product', 
                colHeader: 'Product', 
                colType: 'text', 
                editable: false },
            {
                colName: 'lease_name',
                actualColName: 'lease_id',
                subTable: 'crude_schedule_pickup',
                colHeader: 'Lease',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: leaseOptions,
                dependsOn: 'producer_id',
                getFilteredOptions: (producerId: number, allLeases: any[]) => {
                    const filtered = allLeases.filter(lease => {
                        return Number(lease.producer_id) === Number(producerId);
                    });
                    return filtered;
                },
                enableSidepanel: true,
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,

            },
            {
                colName: 'destination_name',
                actualColName: 'destination_id',
                subTable: 'crude_schedule_delivery',
                colHeader: 'Destination',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: destinationOptions,
                enableSidepanel: true,
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'is_reroute',
                colHeader: 'Is Reroute?',
                colType: 'boolean',
                textValues: true,
                noAsNull: true,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Yes', label: 'Yes' },
                    { value: 'No', label: 'No' },
                ],
                editable: false,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
                cellColor: [
                    { condition: (rowData: any) => rowData.is_reroute === 1, color: 'var(--color-warning-red)' }
                ],
            },
            {
                colName: 'gross_barrels',
                subTable: 'main_table',
                colHeader: 'Gross Barrels',
                colType: 'number',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
                cellColor: [
                    { condition: (rowData: any) => rowData.gross_barrels > 0 && rowData.gross_barrels < 170, color: 'var(--color-warning-red)' }
                ],
            },
            {
                colName: 'net_barrels',
                subTable: 'main_table',
                colHeader: 'Net Barrels',
                colType: 'number',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'observed_gravity',
                subTable: 'main_table',
                colHeader: 'Observed Gravity',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'corrected_gravity',
                subTable: 'main_table',
                colHeader: 'Corrected Gravity',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'observed_temperature',
                subTable: 'main_table',
                colHeader: 'Observed Temperature',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
                cellColor: [
                    {
                        condition: (rowData: any) => {
                            // Get the month from loadDate
                            const loadDate = new Date(rowData.load_date);
                            const month = loadDate.getMonth();

                            // May through September temp threshold is higher
                            const isSummerMonth = month >= 4 && month <= 8;
                            const temperatureThreshold = isSummerMonth ? 105 : 90;
                            
                            // Determine if we should show warning (only if both values are numbers and different)
                            return rowData.observed_temperature > temperatureThreshold;
                        },
                        color: 'var(--color-warning-red)' 
                    }
                ],
            },
            {
                colName: 'start_temperature',
                subTable: 'main_table',
                colHeader: 'Start Temperature',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'end_temperature',
                subTable: 'main_table',
                colHeader: 'End Temperature',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },                
            {
                colName: 'bsw',
                subTable: 'main_table',
                colHeader: 'BS&W',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
                cellColor: [
                    { condition: (rowData: any) => rowData.bsw > 1, color: 'var(--color-warning-red)' }
                ],
            },
            {
                colName: 'request_notes',
                colHeader: 'Request Notes',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'status_notes',
                colHeader: 'Status Notes',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'observations',
                colHeader: 'RB Notes',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'driver_notes',
                actualColName: 'notes',
                subTable: 'main_table',
                colHeader: 'Driver Notes',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'extra_notes',
                colHeader: 'Extra Driver Notes',
                colType: 'text',
                editable: true,
                
            },
            {
                colName: 'customer_sales_order',
                colHeader: 'Customer Sales Order',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'requestDate',
                colHeader: 'Request Date',
                colType: 'datetime',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'requested_by',
                colHeader: 'Requested By',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'poc',
                colHeader: 'Point of Contact',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'poc_email',
                colHeader: 'POC Email',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'poc_phone',
                colHeader: 'POC Phone',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'requestMethod',
                colHeader: 'Request Method',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Text', label: 'Text' },
                    { value: 'Call', label: 'Call' },
                    { value: 'Email', label: 'Email' },
                    { value: 'Other', label: 'Other' },
                ],
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                rolesNotAllowedToEdit: ["accountant", "accounting_admin", "viewer"],
                approvalLevel: 4,
            },
            {
                colName: 'scheduled_by',
                colHeader: 'Scheduled By',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'log_lact_photo',
                colHeader: 'Log/Lact Photo',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'notes_photo_bsw',
                colHeader: 'Notes/Photo BSW',
                colType: 'text',
                editable: true,
                cellColor: [
                    { 
                        condition: (rowData: any) => {
                            // Function to safely parse a BSW value (handles percentage signs)
                            const parseBswValue = (value: string | number | null | undefined): number | null => {
                                if (value === null || value === undefined) return null;
                                
                                // Convert to string for processing
                                const strValue = String(value);
                                
                                // Skip non-numeric values like "N/A" or "-"
                                if (strValue === "N/A" || strValue === "-" || strValue.trim() === "") return null;
                                
                                // Remove percentage sign if present and parse
                                const numericValue = parseFloat(strValue.replace('%', ''));
                                return isNaN(numericValue) ? null : numericValue;
                            };
                            
                            // Get and parse both values
                            const bswValue = rowData.bsw;
                            const notesPhotoValue = parseBswValue(rowData.notes_photo_bsw);
                            
                            const tolerance = 0.1;
                            
                            // Determine if we should show warning (only if both values are numbers and different)
                            return bswValue !== null &&
                                notesPhotoValue !== null &&
                                Math.abs(bswValue - notesPhotoValue) > tolerance;
                        }, 
                        color: 'var(--color-warning-red)' 
                    }
                ],
            },
            {
                colName: 'pdf_ticket',
                colHeader: 'PDF Ticket',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'photo_vs_ticket',
                colHeader: 'Photo vs Ticket',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'log_gps_match',
                colHeader: 'Log/GPS Match',
                colType: 'text',
                editable: true,
            },
        ],
    },
    bottoms_schedule_table: {
        tableName: 'bottoms_schedule_table',
        sortOrder: { 
            'loadDate': 'desc',
            'customer': 'asc',
        },
        tableSummary: [
            { colName: 'gross_barrels', operation: 'sum', label: 'Gross Barrels' },
            { colName: 'ticket_hours', operation: 'sum', label: 'Ticket Hours' },
        ],
        rowColor: [
            {condition: (rowData: any) => rowData.status === 'Canceled', color: 'var(--color-grey-light-darker)'},
            {condition: (rowData: any) => rowData.status === 'Completed', color: 'var(--color-green-complete)'},
            {condition: (rowData: any) => rowData.status === 'Rejected', color: 'var(--color-red-light)'},
            {condition: (rowData: any) => rowData.status === 'Scheduled', color: 'var(--color-yellow)'},
        ],
        warningRules:[
            {
                key: 'bottoms_schedule_table_duplicate_ticket_number',
                type: 'alert',
                colName: 'ticket_number',
                description: 'Duplicate ticket number',
                condition: (rowData: any) => {
                    return rowData.is_duplicate === 1;
                },
            },
        ],
        columns: [
            {
                colName: 'id',
                colHeader: 'ID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'rid',
                colHeader: 'RID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'loadDate',
                colHeader: 'Load Date',
                colType: 'date',
                editable: true,
            },
            {
                colName: 'producer_name',
                colHeader: 'Customer',
                actualColName: 'producer_id',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: producerOptions,
            },
            {
                colName: 'lease_name',
                colHeader: 'Location',
                actualColName: 'lease_id',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: leaseOptions,
                dependsOn: 'producer_id',
                getFilteredOptions: (producerId: number, allLeases: any[]) => {
                    const filtered = allLeases.filter(lease => {
                        return Number(lease.producer_id) === Number(producerId);
                    });
                    return filtered;
                },
            },
            {
                colName: 'destination_name',
                actualColName: 'destination_id',
                colHeader: 'Destination',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: destinationOptions,
            },
            {
                colName: 'carrier_name',
                actualColName: 'carrier_id',
                colHeader: 'Carrier',
                colType: 'text',
                editable: true,
                editableLevel: 2,
                inputType: 'dropdown',
                dropdownOptions: carrierOptions.filter((c: any) => {
                        try {
                            const types: { type: string }[] = typeof c.approved_product_types === 'string'
                                ? JSON.parse(c.approved_product_types)
                                : c.approved_product_types;
                            return Array.isArray(types) && types.some(t => t.type === 'Water');
                        } catch { return false; }
                    }),
            },
            {
                colName: 'status',
                colHeader: 'Status',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Scheduled', label: 'Scheduled' },
                    { value: 'Completed', label: 'Completed' },
                    { value: 'Canceled', label: 'Canceled' },
                    { value: 'Rejected', label: 'Rejected' },
                ],
            },
            {
                colName: 'rockbridge_invoice',
                colHeader: 'RB Invoice',
                colType: 'text',
                editable: true,
                maxWidth: 100,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'carrier_invoice',
                colHeader: 'Carrier Invoice',
                colType: 'text',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'truck_ticket',
                colHeader: 'Truck Ticket',
                colType: 'text',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'ot_ticket',
                colHeader: 'OT Ticket',
                colType: 'text',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'ot_status',
                colHeader: 'OT Status',
                colType: 'text',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'gps_reviewed',
                colHeader: 'GPS Reviewed',
                colType: 'text',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'driver',
                colHeader: 'Driver',
                colType: 'text',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'ticket_hours',
                colHeader: 'Ticket Hours',
                colType: 'number',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'ticket_number',
                colHeader: 'Facility Ticket',
                colType: 'text',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'gross_barrels',
                colHeader: 'Gross Barrels',
                colType: 'number',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'reviewed_by',
                colHeader: 'Reviewed By',
                colType: 'text',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'fresh_water_ticket',
                colHeader: 'FW Ticket',
                colType: 'text',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'fresh_water_volume',
                colHeader: 'FW Volume',
                colType: 'number',
                editable: true,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'rb_notes',
                colHeader: 'RB Notes',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'waste_cert_required',
                colHeader: 'Waste Cert Required',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Y', label: 'Y' },
                    { value: 'N', label: 'N' },
                ],
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'wasteType',
                colHeader: 'Waste Type',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'N/A', label: 'N/A' },
                    { value: 'Type 01: Salt Water', label: 'Type 01: Salt Water' },
                    { value: 'Type 02: Oil Base Mud', label: 'Type 02: Oil Base Mud' },
                    { value: 'Type 03: Water-Base Mud', label: 'Type 03: Water-Base Mud' },
                    { value: 'Type 04: Completion Fluids', label: 'Type 04: Completion Fluids' },
                    { value: 'Type 05: Production Pit Sludge', label: 'Type 05: Production Pit Sludge' },
                    { value: 'Type 06: Storage Tank Sludges', label: 'Type 06: Storage Tank Sludges' },
                    { value: 'Type 07: Produced Sand/Solids', label: 'Type 07: Produced Sand/Solids' },
                    { value: 'Type 08: Fresh Water', label: 'Type 08: Fresh Water' },
                    { value: 'Type 09: Rainwater', label: 'Type 09: Rainwater' },
                    { value: 'Type 10: Washout Water', label: 'Type 10: Washout Water' },
                    { value: 'Type 11: Washout Pit Water', label: 'Type 11: Washout Pit Water' },
                    { value: 'Type 12: Gas Plant Waste Solids', label: 'Type 12: Gas Plant Waste Solids' },
                    { value: 'Type 13: Pipeline Water/Waste', label: 'Type 13: Pipeline Water/Waste' },
                    { value: 'Type 14: Commercial Facility Waste', label: 'Type 14: Commercial Facility Waste' },
                    { value: 'Type 15: Oil Spill Waste', label: 'Type 15: Oil Spill Waste' },
                    { value: 'Type 16: Salvageable Hydrocarbons', label: 'Type 16: Salvageable Hydrocarbons' },
                    { value: 'Type 17: Other (Description Required)', label: 'Type 17: Other (Description Required)' },
                ],
            },
            {
                colName: 'type_of_work',
                colHeader: 'Type of Work',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'notes',
                colHeader: 'Notes',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
                editableLevel: 2,
                
            },
            {
                colName: 'est_hours',
                colHeader: 'Estimated On-Site Hours',
                colType: 'number',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'est_arrival',
                colHeader: 'Expected Arrival Time',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'poc',
                colHeader: 'POC',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'poc_email',
                colHeader: 'POC Email',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'poc_phone',
                colHeader: 'POC Phone',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'requestDate',
                colHeader: 'Created On',
                colType: 'date',
                editable: false,
            },
            {
                colName: 'scheduledBy',
                colHeader: 'Created By',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'requestMethod',
                colHeader: 'Request Method',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
        ]
    },
    ngl_schedule_table: {
        tableName: 'ngl_schedule_table',
        sortOrder: { 
            'loadDate': 'desc',
            'customer': 'asc',
        },
        tableSummary: [
            { colName: 'loaded_gross_volume', operation: 'sum', label: 'Loaded Gross Volume' },
            { colName: 'unloaded_gross_volume', operation: 'sum', label: 'Unloaded Gross Volume' },
            { colName: 'load_demurrage_time', operation: 'sum', label: 'Pickup Demurrage (minutes)' },
            { colName: 'offload_demurrage_time', operation: 'sum', label: 'Offload Demurrage (minutes)' },
            { colName: 'c1_percentage', operation: 'sum', label: 'C1 Volume' },
            { colName: 'c2_percentage', operation: 'sum', label: 'C2 Volume' },
            { colName: 'c3_percentage', operation: 'sum', label: 'C3 Volume' },
            { colName: 'iso_c4_percentage', operation: 'sum', label: 'iC4 Volume' },
            { colName: 'c4_percentage', operation: 'sum', label: 'nC4 Volume' },
            { colName: 'c5_percentage', operation: 'sum', label: 'C5 Volume' },
        ],
        rowColor: [
            {condition: (rowData: any) => rowData.status === 'Canceled', color: 'var(--color-grey-light-darker)'},
            {condition: (rowData: any) => rowData.status === 'Completed', color: 'var(--color-green-complete)'},
            {condition: (rowData: any) => rowData.status === 'Rejected', color: 'var(--color-red-light)'},
            {condition: (rowData: any) => rowData.status === 'Scheduled', color: 'var(--color-yellow)'},
        ],
        warningRules:[
            {
                key: 'ngl_schedule_table_invalid_range',
                type: 'alert',
                colName: 'loaded_gross_volume',
                description: 'Invalid Range',
                condition: (rowData: any) => {
                    if (rowData.status !== 'Completed') return false;

                    // hasMismatch will be true if the difference is > 1
                    if (rowData.loaded_gross_volume < 0 || rowData.loaded_gross_volume > 10000) {
                        return true;
                    }
                    return false;
                },
            },
            {
                key: 'ngl_schedule_table_duplicate_ticket_number',
                type: 'alert',
                colName: 'ticket_number',
                description: 'Duplicate ticket number',
                condition: (rowData: any) => {
                    return rowData.is_duplicate === 1;
                },
            },
            {
                key: 'ngl_schedule_table_pickup_dropoff_discrepancy',
                type: 'alert',
                colName: 'loaded_gross_volume',
                description: 'Pickup/Dropoff Discrepancy Threshold Exceeded',
                condition: (rowData: any) => {
                    if(rowData.status !== 'Completed' || rowData.unloaded_gross_volume === undefined) return false;
                    return Math.abs(rowData.loaded_gross_volume - rowData.unloaded_gross_volume) >= 500;
                },
            }
        ],
        columns: [
            {
                colName: 'id',
                colHeader: 'ID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'rid',
                colHeader: 'RID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'ticket_number',
                colHeader: 'Ticket Number',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'loadDate',
                colHeader: 'Load Date',
                colType: 'date',
                editable: true,
            },
            {
                colName: 'producer_name',
                actualColName: 'producer_id',
                colHeader: 'Producer',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel:2,
                approvalLevel: 4,
                inputType: 'dropdown',
                dropdownOptions: producerOptions,
            },
            {
                colName: 'lease_name',
                actualColName: 'lease_id',
                colHeader: 'Lease',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: leaseOptions,
                dependsOn: 'producer_id',
                getFilteredOptions: (producerId: number, allLeases: any[]) => {
                    const filtered = allLeases.filter(lease => {
                        return Number(lease.producer_id) === Number(producerId);
                    });
                    return filtered;
                },
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                approvalLevel: 4,

            },
            {
                colName: 'destination_name',
                actualColName: 'destination_id',
                colHeader: 'Destination',
                colType: 'text',
                inputType: 'dropdown',
                dropdownOptions: destinationOptions,
                editable: true,
                requiresApproval: true,
                editableLevel: 2,
                approvalLevel: 4,
            },
            {
                colName: 'carrier_name',
                actualColName: 'carrier_id',
                colHeader: 'Carrier',
                colType: 'text',
                editable: true,
                requiresApproval: true,
                editableLevel:2,
                approvalLevel: 4,
                inputType: 'dropdown',
                dropdownOptions: carrierOptions,
                dependsOn: 'lease_id',
                getFilteredOptions: (leaseId: any, allCarriers: any[]) => {
                    if (!leaseId) return allCarriers;
                    const lease = leaseOptions.find((l: any) => l.value == leaseId);
                    if (!lease) return allCarriers;
                    const product = lease.product_type;
                    const region  = lease.region;
                    return allCarriers.filter((carrier: any) => {
                        const productMatch = (() => {
                            //if (!product) return true;
                            try {
                                const types: { type: string }[] = typeof carrier.approved_product_types === 'string'
                                    ? JSON.parse(carrier.approved_product_types)
                                    : carrier.approved_product_types;
                                if (!Array.isArray(types) || types.length === 0) return false;
                                return types.some(t => t.type === product);
                            } catch { return false; }
                        })();
                        const regionMatch = (() => {
                            if (!region) return true;
                            try {
                                const basins: { basin: string }[] = typeof carrier.operating_basin === 'string'
                                    ? JSON.parse(carrier.operating_basin)
                                    : carrier.operating_basin;
                                if (!Array.isArray(basins) || basins.length === 0) return true;
                                return basins.some(b => b.basin === region);
                            } catch { return true; }
                        })();
                        return productMatch && regionMatch;
                    });
                },
            },
            {
                colName: 'driver',
                colHeader: 'Driver',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'status',
                colHeader: 'Status',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Scheduled', label: 'Scheduled' },
                    { value: 'Completed', label: 'Completed' },
                    { value: 'Canceled', label: 'Canceled' },
                    { value: 'Rejected', label: 'Rejected' },
                ],
            },
            {
                colName: 'notes',
                colHeader: 'RB Notes',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'driver_notes',
                colHeader: 'Driver Notes',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'customer_sales_order',
                colHeader: 'Customer Sales Order',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'loaded_gross_volume',
                colHeader: 'Loaded Gross Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName: 'loaded_net_volume',
                colHeader: 'Loaded Net Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName: 'unloaded_gross_volume',
                colHeader: 'Unloaded Gross Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName: 'unloaded_net_volume',
                colHeader: 'Unloaded Net Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName: 'offload_plant_ticket',
                colHeader: 'Offload Plant Ticket',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'load_demurrage_time',
                colHeader: 'Load Demurrage Time (minutes)',
                colType: 'number',
                rolesNotAllowedToEdit:["viewer"],
                editable: true,
            },
            {
                colName: 'offload_demurrage_time',
                colHeader: 'Offload Demurrage Time (minutes)',
                colType: 'number',
                rolesNotAllowedToEdit:["viewer"],
                editable: true,
            },
            {
                colName: 'total_demurrage_charge',
                colHeader: 'Total Demurrage Charge',
                colType: 'number',
                rolesNotAllowedToEdit:["scheduler", "viewer"],
                editable: true,
            },
            {
                colName: 'c1_percentage',
                colHeader: 'C1 Volume',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'c2_percentage',
                colHeader: 'C2 Volume',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'c3_percentage',
                colHeader: 'C3 Volume',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'iso_c4_percentage',
                colHeader: 'iso-C4 Volume',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'c4_percentage',
                colHeader: 'C4 Volume',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'c5_percentage',
                colHeader: 'C5+ Volume',
                colType: 'number',
                editable: false,
            },
            {
                colName: 'market_c1_volume',
                colHeader: 'Market C1 Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName: 'market_c2_volume',
                colHeader: 'Market C2 Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName: 'market_c3_volume',
                colHeader: 'Market C3 Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName: 'market_iso_c4_volume',
                colHeader: 'Market iC4 Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName: 'market_c4_volume',
                colHeader: 'Market C4 Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName: 'market_c5_volume',
                colHeader: 'Market C5+ Volume',
                colType: 'number',
                editable: true,
            },
            {
                colName:'requestDate',
                colHeader: 'Request Date',
                colType: 'date',
                editable: true,
            },
            {
                colName: 'driver_ticket_received',
                colHeader: 'Driver Ticket Received',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'offload_ticket_received',
                colHeader: 'Offload Ticket Received',
                colType: 'text',
                editable: true,
            }
        ],
    },
    // ── Vac Truck Schedule (Phase 2.2) ────────────────────────────────────────
    vac_schedule_table_vs: {
        tableName: 'vac_schedule_table_vs',
        sortOrder: { loadDate: 'desc' },
        tableSummary: [
            { colName: 'est_hours',              operation: 'sum', label: 'Total Est. Hours',  decimals: 1 },
            { colName: 'oi_line_item_count',      operation: 'sum', label: 'OI Line Items',     decimals: 0 },
            { colName: 'oi_total_hours',          operation: 'sum', label: 'OI Hours',          decimals: 6 },
            { colName: 'oi_total_disposal_vol',   operation: 'sum', label: 'Disposal Vol (bbl)', decimals: 1 },
            { colName: 'oi_total_freshwater_vol', operation: 'sum', label: 'Freshwater (gal)',   decimals: 1 },
        ],
        rowColor: [
            {
                condition: (r: any) =>
                    (r.oi_producer_id != null && r.producer_id !== r.oi_producer_id) ||
                    (r.oi_lease_id    != null && r.location_id !== r.oi_lease_id)    ||
                    (r.oi_carrier     != null && !carriersMatch(r.carrier_name, r.oi_carrier)),
                color: '#fff8f0',
            },
            { condition: (r: any) => r.status === 'Cancelled', color: 'var(--color-grey-light-darker)' },
            { condition: (r: any) => r.status === 'Completed' && r.oi_status ==='APPROVED', color: 'var(--color-green-complete)' },
        ],
        warningRules: [
            // Phase 2.2 — OI header mismatch flags
            {
                key: 'vac_schedule_table_vs_producer_mismatch',
                type: 'alert',
                colName: 'producer_name',
                description: 'Producer mismatch vs OI receipt',
                condition: (r: any) => r.oi_producer_id != null && r.producer_id !== r.oi_producer_id,
            },
            {
                key: 'vac_schedule_table_vs_lease_mismatch',
                type: 'alert',
                colName: 'lease_name',
                description: 'Lease mismatch vs OI receipt',
                condition: (r: any) => r.oi_lease_id != null && r.location_id !== r.oi_lease_id,
            },
            {
                key: 'vac_schedule_table_vs_service_date_mismatch',
                type: 'alert',
                colName: 'loadDate',
                description: 'Service date mismatch vs OI receipt',
                condition: (r: any) => {
                    if (r.oi_service_date == null) return false;
                    const toDay = (v: any) => new Date(v).toISOString().slice(0, 10);
                    return toDay(r.loadDate) !== toDay(r.oi_service_date);
                },
            },
            {
                key: 'vac_schedule_table_vs_carrier_mismatch',
                type: 'alert',
                colName: 'carrier_name',
                description: 'Carrier mismatch vs OI receipt',
                condition: (r: any) => r.oi_carrier != null && !carriersMatch(r.carrier_name, r.oi_carrier),
            },
            // {
            //     type: 'alert',
            //     colName: 'oi_line_item_count',
            //     description: 'Line item count mismatch — open side panel to reconcile',
            //     condition: (r: any) =>
            //         r.oi_line_item_count != null &&
            //         r.schedule_li_count !== r.oi_line_item_count,
            // },
            {
                key: 'vac_schedule_table_vs_ot_ticket_no_oi_receipt',
                type: 'alert',
                colName: 'ot_ticket',
                description: 'OT ticket entered but no OI receipt found — sync may be pending',
                condition: (r: any) =>
                    r.ot_ticket != null &&
                    r.ot_ticket !== '' &&
                    (!r.oi_line_items || r.oi_line_items.length === 0),
            },
            {
                key: 'vac_schedule_table_vs_completed_no_ot_ticket',
                type: 'warning',
                colName: 'truck_ticket',
                description: 'Completed job has no OT ticket number',
                condition: (r: any) => r.status === 'Completed' && !r.truck_ticket,
            },
            {
                key: 'vac_schedule_table_vs_line_item_volume_exceeds_150',
                type: 'warning',
                colName: 'oi_disposal_location',
                description: 'Line item volume exceeds 150 bbl — verify ticket',
                condition: (r: any) =>
                    Array.isArray(r.oi_line_items) &&
                    r.oi_line_items.some((li: any) => li.oi_volume != null && li.oi_volume > 150),
            },

        ],
        columns: [
            {
                colName: 'id',
                colHeader: 'ID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'loadDate',
                colHeader: 'Load Date',
                colType: 'date',
                editable: true,
            },
            {
                colName: 'rid',
                colHeader: 'RID',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'rockbridge_invoice',
                colHeader: 'RB Invoice',
                colType: 'text',
                editable: true,
                maxWidth: 100,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'status',
                colHeader: 'Ops Status',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Scheduled',  label: 'Scheduled' },
                    { value: 'In Progress', label: 'In Progress' },
                    { value: 'Completed',  label: 'Completed' },
                    { value: 'Cancelled',  label: 'Cancelled' },
                ],
                cellColor: [
                    { condition: (r: any) => r.status === 'Completed',   color: 'var(--color-green-complete)' },
                    { condition: (r: any) => r.status === 'Scheduled',   color: 'var(--color-yellow)' },
                    { condition: (r: any) => r.status === 'In Progress', color: '#fff8dc' },
                    { condition: (r: any) => r.status === 'Cancelled',   color: 'var(--color-grey-light-darker)' },
                ],
            },
            {
                colName: 'producer_name',
                colHeader: 'Producer',
                actualColName: 'producer_id',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: producerOptions,
            },
            {
                colName: 'lease_name',
                colHeader: 'Lease',
                actualColName: 'lease_id',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: leaseOptions,
                dependsOn: 'producer_id',
                getFilteredOptions: (producerId: number, allLeases: any[]) => {
                    return allLeases.filter(lease => Number(lease.producer_id) === Number(producerId));
                },
            },
            {
                colName: 'carrier_name',
                colHeader: 'Carrier',
                actualColName: 'carrier_id',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: carrierOptions.filter((c: any) => {
                        try {
                            const types: { type: string }[] = typeof c.approved_product_types === 'string'
                                ? JSON.parse(c.approved_product_types)
                                : c.approved_product_types;
                            return Array.isArray(types) && types.some(t => t.type === 'Water');
                        } catch { return false; }
                    }),
            },
            {
                colName:'driver',
                colHeader: 'Driver',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'oi_total_freshwater_vol',
                colHeader: 'Fresh Volume',
                colType: 'number',
                editable: false,
                cellColor: [
                    { condition: (r: any) => r.oi_total_freshwater_vol > 1000, color: '#fff3cd' },
                ],
            },
            {
                colName: 'oi_total_disposal_vol',
                colHeader: 'Disposal Volume',
                colType: 'number',
                editable: false,
                cellColor: [
                    { condition: (r: any) => r.oi_total_disposal_vol > 1000, color: '#fff3cd' },
                ],
            },
            {
                colName: 'oi_total_hours',
                colHeader: 'Total Hours',
                decimals: 6,
                colType: 'number',
                editable: false,
                 cellColor: [
                    { condition: (r: any) => r.oi_total_hours > 15, color: '#fff3cd' },
                ],
            },
            {
                colName: 'ot_ticket',
                colHeader: 'OT Ticket',
                colType: 'text',
                enableSidepanel: true,
                editable: true,
                inputType: 'typeahead',
                dropdownOptions: orphanedTicketOptions.map(t => ({
                    value: t.oi_ticket_number,
                    label: t.oi_ticket_number,
                })),
            },
            {
                colName: 'oi_status',
                colHeader: 'OT Status',
                colType: 'text',
                editable: false,
                cellColor: [
                    { condition: (r: any) => r.oi_status === 'Approved',  color: '#d4edda' },
                    { condition: (r: any) => r.oi_status === 'Paid',      color: '#cce5ff' },
                    { condition: (r: any) => r.oi_status === 'Submitted', color: '#fff3cd' },
                    { condition: (r: any) => r.oi_status === 'Disputed',  color: '#f8d7da' },
                    { condition: (r: any) => r.oi_status === 'Partial',   color: '#e2d9f3' },
                ],
            },
            {
                colName: 'oi_invoice_number',
                colHeader: 'OI (Carrier) Invoice',
                colType: 'text',
                editable: false,
            },
            {
                colName: 'oi_disposal_location',
                colHeader: 'Disposal Location',
                colType: 'text',
                editable: false,
                cellColor: [
                    { condition: (r: any) => r.oi_disposal_location === 'Varied', color: '#fff3cd' },
                ],
            },
            {
                colName: 'est_hours',
                colHeader: 'Est. Hours',
                colType: 'number',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
                decimals: 1,
            },
            {
                colName: 'rb_notes',
                colHeader: 'Internal (RB) Notes',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'customer_sales_order',
                colHeader: 'Customer Sales Order',
                colType: 'text',
                editable: true,
            },
            {
                colName: 'type_of_work',
                colHeader: 'Type of Work',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'waste_cert_required',
                colHeader: 'Waste Cert Required',
                colType: 'text',
                editable: true,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'Y', label: 'Y' },
                    { value: 'N', label: 'N' },
                ],
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
            {
                colName: 'waste_type',
                colHeader: 'Waste Type',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'notes',
                colHeader: 'Notes',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
                editableLevel: 2,
            },
            {
                colName: 'est_arrival',
                colHeader: 'Expected Arrival Time',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'poc',
                colHeader: 'POC',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'poc_email',
                colHeader: 'POC Email',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'poc_phone',
                colHeader: 'POC Phone',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'requestDate',
                colHeader: 'Created On',
                colType: 'date',
                editable: false,
            },
            // {
            //     colName: 'scheduledBy',
            //     colHeader: 'Created By',
            //     colType: 'text',
            //     editable: false,
            // },
            {
                colName: 'requestMethod',
                colHeader: 'Request Method',
                colType: 'text',
                editable: (rowData: any) => rowData.requestMethod !== 'App',
            },
            {
                colName: 'reviewed_by',
                colHeader: 'Reviewed By',
                colType: 'text',
                editable: false,
                rolesNotAllowedToEdit:["scheduler", "viewer"],
            },
        ],
    },
    });

    if (userRole === 'scheduler' || userRole === 'schedule_admin') {
        // reorderColumns only reorders — anything not named in the order list is
        // appended at the end, not dropped — so carrier_selection_delta (an
        // admin/finance-only ops-tracking field, not part of the scheduler's
        // column set) is explicitly filtered out here rather than just left unordered.
        configs.schedule_table.columns = reorderColumns(configs.schedule_table.columns, SCHEDULER_CRUDE_COLUMN_ORDER)
            .filter(col => col.colName !== 'carrier_selection_delta');
        configs.schedule_table.exportColumnOrder = SCHEDULER_CRUDE_EXPORT_COLUMN_ORDER;
    }

    return configs;
};